import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { LENDER_CHANNELS_API, LenderChannelOption } from '../tokens/lender-channels.token';
import {
  DashboardRepaymentRow,
  DashboardStats,
  ExposureChannelRow,
  LenderDashboard,
  LenderDashboardApi,
  ReportJob,
  RiskSlice,
} from '../tokens/lender-dashboard.token';

// ── Backend wire shapes (the real DTOs emitted by the epic backend) ───────────

/** {@code GET /api/v1/lender/portfolio} row — projection over the lender's Loan book (B6). */
interface PortfolioRow {
  loanReference: string;
  borrowerCui: string;
  borrowerName: string;
  product: string;
  currency: string;
  principal: number;
  outstandingBalance: number;
  interestRate: number;
  maturityDate: string;
  status: 'ACTIVE' | 'REPAID' | 'DEFAULTED' | 'RESTRUCTURED' | 'OVERDUE' | string;
  riskGrade: 'AAA' | 'AAB' | 'ABB' | 'BBB' | 'BBC' | 'BCC' | 'CCC' | null;
  edId: number | null;
  edName: string | null;
}

/** {@code GET /api/v1/lender/repayments?range=} aggregate (B6). */
interface RepaymentsAggregate {
  upcoming: RepaymentRow[];
  overdue: RepaymentRow[];
  upcomingTotal: number;
  overdueTotal: number;
}
interface RepaymentRow {
  loanReference: string;
  borrowerName: string;
  instalmentNumber: number;
  dueDate: string;
  totalDue: number;
  status: string;
}

/** {@code GET /api/v1/exposure/limits/{channel}} per-channel limits + utilization (B3). */
interface ExposureLimits {
  channel: string;
  allocatedLimit: number | null;
  utilized: number;
  available: number;
  utilizationPct: number;
  activeSmes: number;
  avgRisk: string;
  sublimits: { perSme: number | null; monthlyOrigination: number | null };
}

/** The async report-job acknowledgement (B6). */
interface ReportJobDto {
  jobId: string;
  type: string;
  format: string | null;
  status: string;
}

/** PRD §5.1 risk-grade donut bands, grade → band + colour. */
const RISK_BANDS: { band: string; grades: string[]; color: string }[] = [
  { band: 'AAA-AAB', grades: ['AAA', 'AAB'], color: '#2E7D32' },
  { band: 'ABB', grades: ['ABB'], color: '#26A69A' },
  { band: 'BBB', grades: ['BBB'], color: '#C8A23C' },
  { band: 'BBC-BCC', grades: ['BBC', 'BCC'], color: '#EF6C00' },
  { band: 'CCC/Default', grades: ['CCC'], color: '#D32F2F' },
];

/**
 * REST-only implementation of {@link LenderDashboardApi} (EM-699 / F1 — EM-706).
 *
 * Composes the SM/CRO dashboard from the live endpoints the backend already exposes
 * (Rule 10 — preferred client-side synthesis; there is no `GET /lender/dashboard`):
 *   - the lender portfolio (B6) drives the five stat cards, the risk-distribution
 *     donut (grouped by {@code riskGrade}) and the exposure-by-channel rows
 *     (grouped by {@code edName});
 *   - the repayments aggregate (B6) drives the Upcoming Repayments widget;
 *   - the per-channel exposure limits (B3) supply the allocated-limit bars and the
 *     Unutilized stat.
 *
 * The channel list is the server-resolved catalogue ({@link LENDER_CHANNELS_API}, EM-1613): the
 * General channel plus one entry per REAL Enterprise Distributor. It used to be a hard-coded
 * `marketplace | metro | selgros | arabesque` constant, so the Exposure-by-E-D-Partner widget listed
 * three partners that exist on no environment and the "Unutilized" KPI summed their fictitious limits.
 *
 * Best-effort per source: a failing widget source resolves to empty so the shell
 * still renders. The portfolio is the primary source — if it fails the call errors
 * and the component shows the retry banner (Rule 09). Rides the Keycloak bearer.
 */
@Injectable()
export class LenderDashboardRestService implements LenderDashboardApi {
  private readonly http = inject(HttpClient);
  private readonly channelsApi = inject(LENDER_CHANNELS_API);

  getDashboard(): Observable<LenderDashboard> {
    const portfolio$ = this.http.get<PortfolioRow[]>(API_PATHS.LENDER_SERVICING_PORTFOLIO, {
      params: new HttpParams().set('size', '1000'),
    });
    const repayments$ = this.http
      .get<RepaymentsAggregate>(API_PATHS.LENDER_REPAYMENTS, { params: new HttpParams().set('range', '7d') })
      .pipe(catchError(() => of<RepaymentsAggregate>({ upcoming: [], overdue: [], upcomingTotal: 0, overdueTotal: 0 })));

    return this.channelsApi.getChannels().pipe(
      switchMap(channels => {
        const limits$ =
          channels.length === 0
            ? of<(ExposureLimits | null)[]>([])
            : forkJoin(
                channels.map(c =>
                  this.http
                    .get<ExposureLimits>(`${API_PATHS.EXPOSURE_LIMITS}/${c.channel}`)
                    .pipe(catchError(() => of<ExposureLimits | null>(null))),
                ),
              );
        return forkJoin({ portfolio: portfolio$, repayments: repayments$, limits: limits$ }).pipe(
          map(({ portfolio, repayments, limits }) => this.compose(portfolio ?? [], repayments, limits, channels)),
        );
      }),
    );
  }

  requestReport(type: string, format?: string): Observable<ReportJob> {
    return this.http
      .post<ReportJobDto>(`${API_PATHS.LENDER_REPORTS}/${type}`, format ? { format } : {})
      .pipe(map(j => ({ jobId: j.jobId, type: j.type, format: j.format, status: j.status })));
  }

  // ── Composition ────────────────────────────────────────────────────────────

  private compose(
    portfolio: PortfolioRow[],
    repayments: RepaymentsAggregate,
    limits: (ExposureLimits | null)[],
    channels: LenderChannelOption[],
  ): LenderDashboard {
    const currency = portfolio[0]?.currency ?? 'EUR';

    // ── Stat cards ──
    const totalExposure = sum(portfolio.map(r => r.outstandingBalance));
    const performing = sum(
      portfolio.filter(r => r.status === 'ACTIVE' || r.status === 'RESTRUCTURED').map(r => r.outstandingBalance),
    );
    const overdue = sum(portfolio.filter(r => r.status === 'OVERDUE').map(r => r.outstandingBalance));
    const npl = sum(portfolio.filter(r => r.status === 'DEFAULTED').map(r => r.outstandingBalance));
    const allocatedTotal = sum(limits.map(l => l?.allocatedLimit ?? 0));
    const unutilized = Math.max(0, allocatedTotal - totalExposure);
    const stats: DashboardStats = { totalExposure, performing, unutilized, overdue, npl };

    // ── Risk distribution donut (group outstanding by grade band) ──
    const riskDistribution: RiskSlice[] = RISK_BANDS.map(b => {
      const exposure = sum(
        portfolio.filter(r => r.riskGrade != null && b.grades.includes(r.riskGrade)).map(r => r.outstandingBalance),
      );
      return {
        band: b.band,
        exposure,
        sharePct: totalExposure > 0 ? round1((exposure / totalExposure) * 100) : 0,
        color: b.color,
      };
    }).filter(s => s.exposure > 0);

    // ── Exposure by E-D partner / channel ──
    const exposureByChannel = this.composeChannels(portfolio, limits, channels);

    // ── Upcoming repayments (upcoming first, then overdue flagged) ──
    const upcomingRepayments: DashboardRepaymentRow[] = [
      ...repayments.overdue.map(r => this.toRepaymentRow(r, true)),
      ...repayments.upcoming.map(r => this.toRepaymentRow(r, false)),
    ];

    return { stats, riskDistribution, exposureByChannel, upcomingRepayments, currency };
  }

  private composeChannels(
    portfolio: PortfolioRow[],
    limits: (ExposureLimits | null)[],
    channels: LenderChannelOption[],
  ): ExposureChannelRow[] {
    const limitByChannel = new Map<string, ExposureLimits>();
    for (const l of limits) {
      if (l) limitByChannel.set(l.channel, l);
    }
    // Aggregate portfolio outstanding + distinct SMEs per channel, keyed on the row's real
    // `edId` (EM-1613 — the pre-fix code matched the E-D's NAME against three demo substrings,
    // so every real partner's exposure silently fell into the marketplace bucket).
    const channelByEdId = new Map<number, string>();
    const generalChannel = channels.find(c => c.general)?.channel ?? 'marketplace';
    for (const c of channels) {
      if (c.edId != null) channelByEdId.set(c.edId, c.channel);
    }
    const expByChannel = new Map<string, number>();
    const smesByChannel = new Map<string, Set<string>>();
    for (const r of portfolio) {
      const key = (r.edId != null ? channelByEdId.get(r.edId) : undefined) ?? generalChannel;
      expByChannel.set(key, (expByChannel.get(key) ?? 0) + r.outstandingBalance);
      if (!smesByChannel.has(key)) smesByChannel.set(key, new Set());
      smesByChannel.get(key)!.add(r.borrowerCui);
    }

    return channels.map(c => {
      const limit = limitByChannel.get(c.channel) ?? null;
      const exposure = expByChannel.get(c.channel) ?? (limit ? limit.utilized : 0);
      const allocated = limit?.allocatedLimit ?? null;
      const smes = smesByChannel.get(c.channel)?.size ?? (limit ? limit.activeSmes : null);
      const utilizationPct =
        allocated && allocated > 0
          ? clamp((exposure / allocated) * 100)
          : limit
            ? clamp(limit.utilizationPct)
            : 0;
      return {
        channel: c.channel,
        name: c.name,
        smeCount: smes ?? null,
        exposure,
        limit: allocated,
        utilizationPct: round1(utilizationPct),
      };
    });
  }

  private toRepaymentRow(r: RepaymentRow, overdue: boolean): DashboardRepaymentRow {
    return {
      loanReference: r.loanReference,
      borrowerName: r.borrowerName,
      dueDate: r.dueDate,
      amount: r.totalDue,
      overdue,
    };
  }
}

// ── pure helpers ──────────────────────────────────────────────────────────────
function sum(xs: number[]): number {
  return xs.reduce((a, b) => a + (b ?? 0), 0);
}
function round1(x: number): number {
  return Math.round(x * 10) / 10;
}
function clamp(x: number): number {
  return Math.max(0, Math.min(100, x));
}
